import { React, useState, useEffect } from '../../utils/globalImports';
import {
    Dialog, DialogActions, CloseIcon, DialogContent, DialogTitle,
    Divider, IconButton, PhoneInput, Typography, FormControl, ClientContact
} from '../../utils/globalImports';
import { CustomButton, CustomTextField, validationMessages } from '../../utils/globalImports';
import { Autocomplete } from '@mui/material';

interface Contact {
    firstName: string;
    lastName: string;
    contactType: string;
    company: string;
    emailAddress: string;
    mobileNo: string | null;
}

interface ModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (formData: Contact) => void;
    contactData?: Contact;
    clientContacts?: ClientContact[]
}

const CustomAddPartiesInvolvedModal: React.FC<ModalProps> = ({ open, onClose, onSubmit, contactData, clientContacts }) => {
    const [formData, setFormData] = useState<Contact>({
        firstName: '',
        lastName: '',
        contactType: '',
        company: '',
        emailAddress: '',
        mobileNo: ''
    });

    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
        contactType: '',
        emailAddress: '',
        mobileNo: '',
        company: '',
    });

    useEffect(() => {
        if (open) {
            if (contactData) {
                setFormData({
                    firstName: contactData.firstName || '',
                    lastName: contactData.lastName || '',
                    contactType: contactData.contactType || '',
                    company: contactData.company || '',
                    emailAddress: contactData.emailAddress || '',
                    mobileNo: contactData.mobileNo || '',
                });
            } else {
                setFormData({
                    firstName: '',
                    lastName: '',
                    contactType: '',
                    company: '',
                    emailAddress: '',
                    mobileNo: ''
                });
            }
            setErrors({
                contactType: '',
                firstName: '',
                lastName: '',
                mobileNo: '',
                emailAddress: '',
                company: '',
            });
        }
    }, [open, contactData]);

    const validate = () => {
        let isValid = true;
        const nameRegex = /^[A-Za-z\s]+$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const newErrors = { firstName: '', lastName: '', contactType: '', emailAddress: '', mobileNo: '', company: '' };

        if (!formData.firstName.trim()) {
            newErrors.firstName = validationMessages.common_required("First name");
            isValid = false;
        } else if (!nameRegex.test(formData.firstName.trim())) {
            newErrors.firstName = validationMessages.common_invalidString("First name");
            isValid = false;
        } else if (formData.firstName.trim().length > 100) {
            newErrors.firstName = validationMessages.common_maxLength("First name", 100);
            isValid = false;
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = validationMessages.common_required("Last name");
            isValid = false;
        } else if (!nameRegex.test(formData.lastName.trim())) {
            newErrors.lastName = validationMessages.common_invalidString("Last name");
            isValid = false;
        } else if (formData.lastName.trim().length > 100) {
            newErrors.lastName = validationMessages.common_maxLength("Last name", 100);
            isValid = false;
        }

        if (!formData.contactType.trim()) {
            newErrors.contactType = validationMessages.common_required("Type");
            isValid = false;
        }

        if (!formData.emailAddress.trim()) {
            newErrors.emailAddress = validationMessages.common_required("Email Address");
            isValid = false;
        } else if (!emailRegex.test(formData.emailAddress.trim())) {
            newErrors.emailAddress = validationMessages.common_email("Email Address");
            isValid = false;
        }

        if (!formData.company.trim()) {
            newErrors.company = validationMessages.common_required("Company");
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };


    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            onSubmit(formData);
            onClose();
        } else {
            console.log("Validation failed:", errors);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ color: '#1e66ab', textAlign: 'center', fontWeight: 600, fontSize: '16px' }}>
                {contactData ? 'Edit Contact' : 'Add Contact'}
                <IconButton color="inherit" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent>
                <form onSubmit={handleSubmit}>
                    {/* First Name */}
                    <FormControl sx={{ mb: 1 }} fullWidth>
                        <Autocomplete
                            freeSolo
                            options={clientContacts ?? []}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.firstName
                            }
                            inputValue={formData.firstName}
                            onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                    // User typed manually
                                    setFormData((prev) => ({
                                        ...prev,
                                        firstName: newValue,
                                        lastName: '',
                                        emailAddress: '',
                                        mobileNo: '',
                                        company: '',
                                        contactType: ''
                                    }));
                                } else if (newValue) {
                                    // User selected from list
                                    setFormData((prev) => ({
                                        ...prev,
                                        firstName: newValue.firstName,
                                        lastName: newValue.lastName,
                                        emailAddress: newValue.emailAddress,
                                        mobileNo: newValue.mobileNo,
                                        company: newValue.clientName
                                    }));
                                }
                                else {
                                    // User cleared or nothing selected
                                    setFormData((prev) => ({
                                        ...prev,
                                        firstName: '',
                                        lastName: '',
                                        emailAddress: '',
                                        mobileNo: '',
                                        company: '',
                                        contactType: ''
                                    }));
                                }
                            }}
                            onInputChange={(event, newInputValue) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    firstName: newInputValue,
                                }));
                            }}
                            renderInput={(params) => (
                                <div>
                                    <CustomTextField
                                        {...params} // Contains inputProps, InputProps, inputRef, etc.
                                        label="First Name"
                                        name="firstName"
                                        required
                                    />
                                    {errors.firstName && (
                                        <Typography color="error" sx={{ fontSize: '12px', mt: 0.5 }}>
                                            {errors.firstName}
                                        </Typography>
                                    )}
                                </div>
                            )}
                        />
                    </FormControl>

                    {/* Last Name */}
                    <FormControl sx={{ mb: 1 }} fullWidth>
                        <CustomTextField
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required={true}
                        />
                        {errors.lastName && (
                            <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                                {errors.lastName}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Contact Type */}
                    <FormControl sx={{ mb: 1 }} fullWidth>
                        <CustomTextField
                            label="Type"
                            name="contactType"
                            value={formData.contactType}
                            onChange={handleChange}
                            required={true}
                        />
                        {errors.contactType && (
                            <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                                {errors.contactType}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Company */}
                    <FormControl sx={{ mb: 1 }} fullWidth>
                        <CustomTextField
                            label="Company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            required={true}
                        />
                        {errors.company && (
                            <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                                {errors.company}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Email Address */}
                    <FormControl sx={{ mb: 1 }} fullWidth>
                        <CustomTextField
                            label="Email Address"
                            name="emailAddress"
                            type="email"
                            value={formData.emailAddress}
                            onChange={handleChange}
                            required={true}
                        />
                        {errors.emailAddress && (
                            <Typography color="error" sx={{ fontSize: "12px", mt: 0.5 }}>
                                {errors.emailAddress}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Phone Number */}
                    <Typography variant="subtitle1" sx={{ display: 'block', color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1 }}>
                        Phone Number
                    </Typography>
                    <FormControl fullWidth>
                        <PhoneInput
                            country={"gb"}
                            enableSearch={true}
                            value={formData.mobileNo}
                            onChange={(phone) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    mobileNo: phone?.trim() ? phone : "", // Convert null to an empty string
                                }))
                            }
                            inputStyle={{
                                width: "100%",
                                borderRadius: "50px",
                                height: "35px",
                                paddingLeft: "50px",
                                fontSize: "14px",
                            }}
                            containerStyle={{
                                width: "100%",
                            }}
                        />
                    </FormControl>
                </form>
            </DialogContent>

            <DialogActions>
                <CustomButton
                    sx={{
                        textTransform: 'none',
                        width: '40%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        mr: 'auto',
                        ml: 'auto',
                        mb: 1,
                    }}
                    disabled={!formData.firstName}
                    backgroundColor="#1e66ab"
                    hoverColor="rgba(150, 193, 33, 0.75)"
                    onClick={() => {
                        setFormData((prev) => ({
                            ...prev,
                            firstName: '',
                            lastName: '',
                            emailAddress: '',
                            mobileNo: '',
                            company: '',
                            contactType: ''
                        }));
                    }}
                >
                    Clear All
                </CustomButton>

                <CustomButton
                    sx={{
                        textTransform: 'none',
                        width: '40%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        mr: 'auto',
                        ml: 'auto',
                        mb: 1,
                    }}
                    onClick={handleSubmit}
                >
                    {contactData ? 'Update' : 'Add'}
                </CustomButton>
            </DialogActions>
        </Dialog>
    );
};

export default CustomAddPartiesInvolvedModal;
